import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ProgressBarProps {
  status: string;
  editable?: boolean;
  onChange?: (status: string) => void;
}

interface StageConfig {
  value: string;
  label: string;
  color: string;
  lightColor: string;
}

const STAGES: StageConfig[] = [
  { value: '0/4', label: '0/4', color: '#000000', lightColor: '#E5E5E5' },
  { value: '1/4', label: '1/4', color: '#000000', lightColor: '#E5E5E5' },
  { value: '2/4', label: '2/4', color: '#000000', lightColor: '#E5E5E5' },
  { value: '3/4', label: '3/4', color: '#000000', lightColor: '#E5E5E5' },
  { value: '4/4', label: '4/4', color: '#000000', lightColor: '#E5E5E5' },
];

function ProgressRing({ progress, color, lightColor, size = 26 }: { progress: number; color: string; lightColor: string; size?: number }) {
  const radius = (size - 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={lightColor}
        strokeWidth="2"
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        stroke={color}
        strokeWidth="2"
        fill="none"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

export function ProgressBar({ status, editable = false, onChange }: ProgressBarProps) {
  const currentIndex = STAGES.findIndex(s => s.value === status);
  const currentStage = STAGES[currentIndex] || STAGES[0];
  const progress = (currentIndex / (STAGES.length - 1)) * 100;

  const handlePrev = () => {
    if (currentIndex > 0 && onChange) {
      onChange(STAGES[currentIndex - 1].value);
    }
  };

  const handleNext = () => {
    if (currentIndex < STAGES.length - 1 && onChange) {
      onChange(STAGES[currentIndex + 1].value);
    }
  };

  if (editable) {
    return (
      <div className="relative w-full h-[26px] flex items-center justify-center gap-1">
        <button
          onClick={handlePrev}
          disabled={currentIndex === 0}
          className={`p-0.5 rounded transition-colors ${
            currentIndex === 0 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
          title="上一阶段"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="relative flex items-center justify-center">
          <ProgressRing
            progress={progress}
            color={currentStage.color}
            lightColor={currentStage.lightColor}
            size={26}
          />
          <span
            className="absolute inset-0 flex items-center justify-center font-bold"
            style={{ color: currentStage.color, fontSize: '10px', lineHeight: '1' }}
          >
            {currentStage.label}
          </span>
        </div>
        <button
          onClick={handleNext}
          disabled={currentIndex === STAGES.length - 1}
          className={`p-0.5 rounded transition-colors ${
            currentIndex === STAGES.length - 1 
              ? 'text-gray-300 cursor-not-allowed' 
              : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
          title="下一阶段"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative w-full h-[26px] flex items-center justify-center">
      <ProgressRing
        progress={progress}
        color={currentStage.color}
        lightColor={currentStage.lightColor}
        size={26}
      />
      <span
        className="absolute inset-0 flex items-center justify-center font-bold"
        style={{ color: currentStage.color, fontSize: '10px', lineHeight: '1' }}
      >
        {currentStage.label}
      </span>
    </div>
  );
}
